import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { AccountActionsModalComponent } from '@rma/authentication/public/orc-account-actions';
import { SwitchAccountComponent } from '@rma/authentication/public/orc-switch-account';
import { CurrentProfileContext } from '@rma/authentication/public/user';
import { AvatarComponent } from '@rma/generic/ui/avatar';
import { RmaIconComponent, RmaIcons } from '@rma/generic/ui/icon';
import { RmaModalService } from '@rma/generic/ui/modal';
import { FeatureFlagService, TempFeatureFlags } from '@rma/generic/util/feature-flags';
import { PossessiveNamePipeModule } from '@rma/generic/util/pipes/possessive-name';

@Component({
  selector: 'rma-nav-accounts-footer',
  templateUrl: './nav-accounts-footer.component.html',
  styles: `
    @use 'rma-no-css' as rma;

    :host {
      max-height: rma.$sideNavAccountsFooterHeight;
      min-height: rma.$sideNavAccountsFooterHeight;
    }

    .collapsed {
      margin: 0;

      button {
        padding: 0 auto;

        &:hover {
          background-color: transparent;
        }
      }
    }
  `,
  host: {
    class: 'block border-t border-steel-10',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, RmaIconComponent, TranslocoModule, AvatarComponent, PossessiveNamePipeModule],
  providers: [provideTranslocoScope('sideNav')],
})
export class AccountsFooterComponent {
  private readonly dialog = inject(RmaModalService);
  private readonly featureService = inject(FeatureFlagService);

  public readonly profile = input.required<CurrentProfileContext>();
  public readonly hasMultipleProfiles = input.required<boolean>();
  public readonly collapsed = input(false);
  public readonly newModal = toSignal(this.featureService.getFeature$(TempFeatureFlags.AgentAssistants, false), {
    initialValue: false,
  });

  public readonly name = computed(() => {
    const { shortName, fullName } = this.profile();
    return shortName ?? fullName;
  });

  protected icon = signal<RmaIcons>('unfold_more');

  public async switchUser() {
    this.icon.set('unfold_less');

    if (this.newModal() && this.hasMultipleProfiles()) {
      this.dialog
        .openFullScreenOnMobileOnly(SwitchAccountComponent, {
          panelClass: ['md:w-[45rem]', 'lg:w-[50rem]', 'md:h-[40rem]'],
          autoFocus: false,
        })
        .afterClosed()
        .subscribe(() => this.icon.set('unfold_more'));
    } else {
      this.dialog
        .openFullScreenOnMobileOnly(AccountActionsModalComponent, {
          minWidth: '20rem',
          autoFocus: false,
        })
        .afterClosed()
        .subscribe(() => this.icon.set('unfold_more'));
    }
  }
}
